/**
 * MatchTierChip — score + tier label pill for the Grant Match Engine (G2A CP3).
 *
 * Frontend Discipline 2 (locked): the match tier chip and the
 * EligibilityBadge stay VISUALLY DISTINCT wherever they appear
 * together. They are never merged into a composite badge.
 *
 * Chip content is EXACTLY one of:
 *   - "{score} · Strong Match"      (90-100)
 *   - "{score} · Good Match"        (75-89)
 *   - "{score} · Fair Match"        (60-74)
 *   - "{score} · Weak Match"        (40-59)
 *   - "{score} · Poor Match"        (0-39)
 *   - "{score} · Partial Analysis"  (fail-closed cap 70)
 *   - "Not Eligible"                (no score — hard-filter short-circuit)
 *
 * The label strings are approved copy — do not paraphrase in
 * downstream renders. Any text change must update `TIER_LABELS`
 * in `models/grant_match.py` in lock-step.
 */
import { HelpCircle, Target, XCircle } from "lucide-react";

const TIER_META = {
  strong_match: { label: "Strong Match", color: "#0F9F70", Icon: Target },
  good_match: { label: "Good Match", color: "#5D50C8", Icon: Target },
  fair_match: { label: "Fair Match", color: "#60A5FA", Icon: Target },
  weak_match: { label: "Weak Match", color: "#FBBF24", Icon: Target },
  poor_match: { label: "Poor Match", color: "#94A3B8", Icon: Target },
  partial_analysis: { label: "Partial Analysis", color: "#94A3B8", Icon: HelpCircle },
  not_eligible: { label: "Not Eligible", color: "#EF4444", Icon: XCircle },
};

const SIZE_CLASSES = {
  sm: "text-[10px] px-2 py-0.5",
  md: "text-xs px-2.5 py-1",
  lg: "text-sm px-3.5 py-1.5",
};

export default function MatchTierChip({
  tier,
  score,
  size = "md",
  testId,
}) {
  const meta = TIER_META[tier] || TIER_META.partial_analysis;
  const Icon = meta.Icon;
  const showScore =
    tier !== "not_eligible" && score !== null && score !== undefined;
  return (
    <span
      data-testid={testId || `match-tier-chip-${tier}`}
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold uppercase tracking-[0.14em] ${SIZE_CLASSES[size]}`}
      style={{
        // Distinct from EligibilityBadge (which uses solid tri-state colors) —
        // the match chip uses a lower-saturation "outlined pill" style so
        // both chips can sit side-by-side on a card without visual conflict.
        background: `${meta.color}14`,
        color: meta.color,
        border: `1px solid ${meta.color}55`,
      }}
    >
      <Icon className="w-3 h-3" />
      {showScore ? `${score} · ${meta.label}` : meta.label}
    </span>
  );
}
